import Configuration from "config";

type CookiebotConsent = NonNullable<Window["Cookiebot"]>["consent"];
type ConsentCategory = Exclude<keyof CookiebotConsent, "necessary" | "stamp">;

export const isCookieConsentEnabled = () => Boolean(Configuration.COOKIEBOT_CBID);

// Reopens the consent banner so the user can change a previous choice.
export function renewCookieConsent() {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Cookiebot is a third-party global that may not have loaded
  window.Cookiebot?.renew?.();
}

// Fired by Cookiebot on page load once the stored consent state is known, and
// again whenever the user submits or changes their consent in the banner.
const CONSENT_EVENTS = [
  "CookiebotOnConsentReady",
  "CookiebotOnAccept",
  "CookiebotOnDecline",
];

// Fired once the Cookiebot script has loaded and knows which regulation applies.
const REGULATION_KNOWN_EVENTS = ["CookiebotOnLoad", "CookiebotOnConsentReady"];

/**
 * Loads the Cookiebot consent banner. Blocking mode is "manual" because the
 * app's trackers are npm-bundled SDKs initialized from code — Cookiebot's
 * automatic mode only intercepts script tags and cannot block them. Each
 * tracker must instead be gated through `bindConsent`.
 *
 * Two configurations serve the app, the same pair the website uses: the GDPR
 * opt-in group everywhere, and for US visitors the CCPA group, where consent
 * is implied and the footer's "do not sell" link is the opt-out. Cookiebot
 * picks by the visitor's location from `data-georegions`.
 */
export function initCookieConsent() {
  if (!Configuration.COOKIEBOT_CBID) {
    return;
  }

  const script = document.createElement("script");
  script.id = "Cookiebot";
  script.src = "https://consent.cookiebot.com/uc.js";
  script.dataset.cbid = Configuration.COOKIEBOT_CBID;
  if (Configuration.COOKIEBOT_US_CBID) {
    script.dataset.georegions = `{'region':'US','cbid':'${Configuration.COOKIEBOT_US_CBID}'}`;
  }
  script.dataset.blockingmode = "manual";
  // Disable the floating "Privacy trigger" icon for this installation only
  // (the domain group setting still applies to the website); consent is
  // reopened via the footer's cookie-preferences link instead.
  script.dataset.widgetEnabled = "false";
  document.head.appendChild(script);
}

/**
 * True when this visitor was not asked to opt in: Cookiebot applied the CCPA
 * configuration, or treated consent as implied because no opt-in regulation
 * applies, which is how a US address is served. Either way the footer must offer
 * the CPRA opt-out.
 */
export function isDoNotSellRegion(): boolean {
  const cookiebot = window.Cookiebot;
  if (!cookiebot) {
    return false;
  }
  // `window.Cookiebot` exists before the script has placed the visitor, and until
  // then `regulations` and `consent` are unset (the SDK typings say otherwise).
  // `method` ("implied" | "explicit") is documented by Cookiebot but missing from the typings.
  const regulations = cookiebot.regulations as
    | Partial<typeof cookiebot.regulations>
    | undefined;
  const consent = cookiebot.consent as { method?: string } | undefined;
  return regulations?.ccpaApplies === true || consent?.method === "implied";
}

// Calls `handler` once, as soon as the consent state is known: now if it already is
// (or no banner is configured), otherwise on Cookiebot's first consent event.
export function onConsentKnown(handler: () => void) {
  const cookiebot = window.Cookiebot;
  if (!Configuration.COOKIEBOT_CBID || cookiebot?.consented || cookiebot?.declined) {
    handler();
    return;
  }
  const once = () => {
    for (const event of CONSENT_EVENTS) {
      window.removeEventListener(event, once);
    }
    handler();
  };
  for (const event of CONSENT_EVENTS) {
    window.addEventListener(event, once);
  }
}

// Calls `handler` once Cookiebot knows the visitor's regulation, and now if it
// already does. Returns the unsubscribe.
export function onRegulationKnown(handler: () => void): () => void {
  for (const event of REGULATION_KNOWN_EVENTS) {
    window.addEventListener(event, handler);
  }
  if (window.Cookiebot?.regulations) {
    handler();
  }
  return () => {
    for (const event of REGULATION_KNOWN_EVENTS) {
      window.removeEventListener(event, handler);
    }
  };
}

// The visitor's answer for `category` right now. Until Cookiebot has reported
// one it counts as not given.
export function hasConsent(category: ConsentCategory): boolean {
  if (!Configuration.COOKIEBOT_CBID) {
    return true;
  }
  const consent = window.Cookiebot?.consent as Partial<CookiebotConsent> | undefined;
  return consent?.[category] === true;
}

/**
 * Calls `onGranted`/`onRevoked` to match the user's consent for `category`,
 * now and whenever it changes. Handlers must be idempotent — they can fire
 * multiple times. Builds without a Cookiebot domain configured assume consent
 * is granted.
 */
export function bindConsent(
  category: ConsentCategory,
  onGranted: () => void,
  onRevoked?: () => void
) {
  if (!Configuration.COOKIEBOT_CBID) {
    onGranted();
    return;
  }

  const sync = () => {
    const consent = window.Cookiebot?.consent;
    if (!consent) {
      return;
    }

    if (consent[category]) {
      onGranted();
    } else {
      onRevoked?.();
    }
  };

  for (const event of CONSENT_EVENTS) {
    window.addEventListener(event, sync);
  }

  sync();
}
